import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export interface PickerGroup { title: string; options: { id: string; name: string; note?: string }[] }

/**
 * Chips + dropdown for one row's regions or profiling answers. Nothing ticked means "the whole target group" for
 * that dimension. Only options the target group already has are offered (the backend rejects any other id).
 */
export function CellPicker({ label, emptyText, groups, selected, onChange }: {
  label: string;
  emptyText: string;
  groups: PickerGroup[];
  selected: string[];
  onChange: (ids: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const names = useMemo(() => Object.fromEntries(groups.flatMap((g) => g.options.map((o) => [o.id, o.name]))), [groups]);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (buttonRef.current?.contains(target) || popRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    const close = () => setOpen(false); // the dropdown is positioned from the button, so scrolling or resizing closes it
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', close);
    window.addEventListener('scroll', close, true);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', close);
      window.removeEventListener('scroll', close, true);
    };
  }, [open]);

  const openPicker = () => {
    if (!open && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      // keep the 16rem-wide dropdown inside the window
      setPos({ top: rect.bottom + 4, left: Math.max(8, Math.min(rect.left, window.innerWidth - 264)) });
    }
    setOpen((o) => !o);
  };

  const toggle = (id: string) => onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);

  return (
    <div>
      <button
        ref={buttonRef}
        type="button"
        aria-label={label}
        aria-haspopup="true"
        aria-expanded={open}
        onClick={openPicker}
        className="flex min-h-[2rem] w-full min-w-[8rem] max-w-[13rem] flex-wrap items-center gap-1 rounded-lg border border-dashed border-line bg-surface px-2 py-1 text-left hover:border-brand-300"
      >
        {selected.length === 0
          ? <span className="text-xs text-muted">{emptyText}</span>
          : selected.map((id) => <span key={id} className="rounded-full bg-brand-50 px-2 py-0.5 text-xs font-semibold text-brand-800">{names[id] || id}</span>)}
      </button>
      {open && createPortal(
        <div ref={popRef} role="group" aria-label={`${label} options`} style={{ top: pos.top, left: pos.left }} className="fixed z-[1060] max-h-64 w-64 overflow-auto rounded-lg border border-line bg-surface p-1.5 shadow-pop">
          {groups.map((g) => (
            <div key={g.title}>
              <p className="px-2 pb-0.5 pt-1.5 text-[11px] font-bold uppercase tracking-wide text-muted">{g.title}</p>
              {g.options.map((o) => (
                <label key={o.id} className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-canvas">
                  <input type="checkbox" className="h-4 w-4 accent-brand-600" checked={selected.includes(o.id)} onChange={() => toggle(o.id)} />
                  <span>{o.name}{o.note && <span className="ml-1 text-xs text-muted">{o.note}</span>}</span>
                </label>
              ))}
            </div>
          ))}
          <p className="mt-1 border-t border-line px-2 py-1.5 text-xs text-muted">None ticked = {emptyText.toLowerCase()}</p>
        </div>,
        document.body
      )}
    </div>
  );
}
